/**
 * Capabilities tab for managing role capabilities
 * Uses capability picker from listCapabilities API
 */
import { Check, X } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";

interface RoleCapabilitiesTabProps {
	role: {
		id: string;
		isSystem: boolean;
		capabilities: string[];
	};
	onUpdate: () => void;
}

/**
 * Capabilities tab for managing role capabilities
 * @param root0 - Component props
 * @param root0.role - The role to manage capabilities for
 * @param root0.onUpdate - Callback when capabilities are updated
 */
export function RoleCapabilitiesTab({
	role,
	onUpdate,
}: RoleCapabilitiesTabProps) {
	const [capabilities, setCapabilities] = useState<string[]>(role.capabilities);

	// Fetch available capabilities
	const capabilitiesQuery = trpc.roles.listCapabilities.useQuery();

	const setCapabilitiesMutation = trpc.roles.setCapabilities.useMutation({
		onSuccess: () => {
			onUpdate();
		},
	});

	/**
	 * Toggles a capability on or off
	 * @param capability - The capability string to toggle
	 */
	const handleToggleCapability = (capability: string) => {
		setCapabilities((prev) =>
			prev.includes(capability)
				? prev.filter((c) => c !== capability)
				: [...prev, capability]
		);
	};

	/**
	 * Saves the capabilities changes
	 */
	const handleSave = () => {
		setCapabilitiesMutation.mutate({
			roleId: role.id,
			capabilities,
		});
	};

	const hasChanges =
		capabilities.length !== role.capabilities.length ||
		capabilities.some((c) => !role.capabilities.includes(c));

	if (capabilitiesQuery.isLoading) {
		return (
			<div className="flex items-center justify-center py-8">
				<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{role.isSystem ? (
				<div className="rounded-md bg-muted p-3 text-sm">
					System role capabilities cannot be modified.
				</div>
			) : (
				<p className="text-sm text-muted-foreground">
					Select the capabilities this role should have. Users with this role will be able to perform actions based on these capabilities.
				</p>
			)}

			{/* Current capabilities summary */}
			<div className="space-y-2">
				<Label>Selected Capabilities ({capabilities.length})</Label>
				<div className="flex flex-wrap gap-2 min-h-[32px] p-2 border rounded-md">
					{capabilities.length === 0 ? (
						<span className="text-muted-foreground text-sm">No capabilities selected</span>
					) : (
						capabilities.map((cap) => (
							<Badge key={cap} variant="secondary" className="pr-1">
								{cap}
								{!role.isSystem && (
									<button
										type="button"
										className="ml-1 rounded-full hover:bg-muted p-0.5"
										onClick={() => { handleToggleCapability(cap); }}
									>
										<X className="h-3 w-3" />
									</button>
								)}
							</Badge>
						))
					)}
				</div>
			</div>

			{/* Capability picker */}
			{!role.isSystem && (
				<div className="space-y-2">
					<Label>Available Capabilities</Label>
					<div className="border rounded-md divide-y max-h-[300px] overflow-y-auto">
						{capabilitiesQuery.data?.map(({ capability, description }) => (
							<label
								key={capability}
								className="flex items-start gap-3 p-3 cursor-pointer hover:bg-muted/50"
							>
								<Checkbox
									checked={capabilities.includes(capability)}
									onCheckedChange={() => { handleToggleCapability(capability); }}
									className="mt-0.5"
								/>
								<div className="flex-1 min-w-0">
									<div className="font-medium text-sm">{capability}</div>
									<div className="text-xs text-muted-foreground">{description}</div>
								</div>
								{capabilities.includes(capability) && (
									<Check className="h-4 w-4 text-primary shrink-0" />
								)}
							</label>
						))}
					</div>
				</div>
			)}

			{/* Error */}
			{setCapabilitiesMutation.isError && (
				<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
					{setCapabilitiesMutation.error.message}
				</div>
			)}

			{!role.isSystem && (
				<Button
					onClick={handleSave}
					disabled={!hasChanges || setCapabilitiesMutation.isPending}
				>
					{setCapabilitiesMutation.isPending ? "Saving..." : "Save Capabilities"}
				</Button>
			)}
		</div>
	);
}
